import { useCallback, useRef, useState } from "react";

interface UseSpeechToTextOptions {
  onResult: (transcript: string) => void;
  onError: (error: string) => void;
  language?: string;
}

interface SpeechToTextReturn {
  isListening: boolean;
  isSupported: boolean;
  start: () => void;
  stop: () => void;
  toggle: () => void;
}

const SpeechRecognitionAPI =
  window.SpeechRecognition ?? window.webkitSpeechRecognition;

function buildOnResultHandler(
  finalTranscriptRef: React.MutableRefObject<string>,
): (event: SpeechRecognitionEvent) => void {
  return (event: SpeechRecognitionEvent) => {
    let finalTranscript = finalTranscriptRef.current;
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const result = event.results[i];
      const text = result[0].transcript;
      if (result.isFinal) {
        finalTranscript += `${text} `;
      }
    }
    finalTranscriptRef.current = finalTranscript;
  };
}

function buildOnErrorHandler(
  onErrorRef: React.MutableRefObject<(error: string) => void>,
): (event: SpeechRecognitionErrorEvent) => void {
  return (event: SpeechRecognitionErrorEvent) => {
    const errorType = event.error;
    // "no-speech" and "aborted" are not real errors
    if (errorType === "no-speech" || errorType === "aborted") return;
    onErrorRef.current?.(`Speech recognition error: ${errorType}`);
  };
}

function buildOnEndHandler(
  finalTranscriptRef: React.MutableRefObject<string>,
  onResultRef: React.MutableRefObject<(transcript: string) => void>,
  setIsListening: (value: boolean) => void,
  recognitionRef: React.MutableRefObject<SpeechRecognition | null>,
): () => void {
  return () => {
    const full = finalTranscriptRef.current.trim();
    if (full) onResultRef.current?.(full);
    finalTranscriptRef.current = "";
    setIsListening(false);
    recognitionRef.current = null;
  };
}

interface StartRecognitionParams {
  language: string;
  finalTranscriptRef: React.MutableRefObject<string>;
  onResultRef: React.MutableRefObject<(transcript: string) => void>;
  onErrorRef: React.MutableRefObject<(error: string) => void>;
  recognitionRef: React.MutableRefObject<SpeechRecognition | null>;
  setIsListening: (value: boolean) => void;
  cleanupRecognition: () => void;
}

function startRecognition({
  language,
  finalTranscriptRef,
  onResultRef,
  onErrorRef,
  recognitionRef,
  setIsListening,
  cleanupRecognition,
}: StartRecognitionParams): void {
  if (!SpeechRecognitionAPI) {
    onErrorRef.current?.("Speech recognition is not supported in this browser");
    return;
  }

  cleanupRecognition();

  try {
    finalTranscriptRef.current = "";
    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = language;
    recognition.onresult = buildOnResultHandler(finalTranscriptRef);
    recognition.onerror = buildOnErrorHandler(onErrorRef);
    recognition.onend = buildOnEndHandler(
      finalTranscriptRef,
      onResultRef,
      setIsListening,
      recognitionRef,
    );
    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  } catch (err) {
    const errorMessage =
      err instanceof Error ? err.message : "Failed to start speech recognition";
    setIsListening(false);
    onErrorRef.current?.(errorMessage);
  }
}

export function useSpeechToText({
  onResult,
  onError,
  language = "en-US",
}: UseSpeechToTextOptions): SpeechToTextReturn {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const finalTranscriptRef = useRef("");
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);
  onResultRef.current = onResult;
  onErrorRef.current = onError;

  const cleanupRecognition = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.onspeechstart = null;
        recognitionRef.current.abort();
      } catch {
        // Ignore cleanup errors
      }
      recognitionRef.current = null;
    }
  }, []);

  const start = useCallback(
    () =>
      startRecognition({
        language,
        finalTranscriptRef,
        onResultRef,
        onErrorRef,
        recognitionRef,
        setIsListening,
        cleanupRecognition,
      }),
    [language, cleanupRecognition],
  );

  const stop = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        // Ignore — onend will fire regardless
      }
    } else {
      setIsListening(false);
    }
  }, []);

  const toggle = useCallback(() => {
    if (isListening) stop();
    else start();
  }, [isListening, start, stop]);

  return {
    isListening,
    isSupported: Boolean(SpeechRecognitionAPI),
    start,
    stop,
    toggle,
  };
}
